import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { actions as userActions } from '../../actions/userActions';
import { actions as accountActions } from '../../actions/accountActions';
import { actions as locationTaskActions } from '../../actions/locationTaskActions';
import './MyProfile.scss'
import AccountDetailsSection from './AccountDetailsSection';
import ConfigurationSection from './ConfigurationSection';
import ResetPasswordSection from './ResetPasswordSection';
import SupportTicketsSection from './SupportTicketsSection';
import CustomerConversationDrawer from '../Support/Customer/CustomerConversationDrawer';
import * as types from '../../constants/actionTypes';
import Notification from '../Common/Notification';
import LoadingIndicator from '../Common/LoadingIndicator/LoadingIndicator';
import { isValidEmailAddress } from '../../helpers/validation';
import Moment from 'moment';
import { DATE_FORMAT, SUPPORT_TICKET_ID_URL_PARAM } from '../../constants/constants';

export class MyProfile extends React.Component {

    constructor(props, context) {
        super(props, context);

        const user = {
            id: 0,
            userKey: '',
            subscriptionId: 0,
            firstName: '',
            lastName: '',
            email: '',
            userName: '',
            workPhone: '',
            jobTitle: '',
            countryId: '',
            defaultLocationId: '',
            managementCompanyId: '',
            departmentIds: '',
            isSSOUser: false,
            ssoId: '',
            employeeId: ''
        }

        this.state = {
            isLoading: false,
            isSavingAccountDetails: false,
            isSavingConfiguration: false,
            isAccountDetailFormValid: true,
            isConfigurationFormValid: true,
            user: user,
            countries: [],
            locations: [],
            departments: [],
            managementCompanies: [],
            supportTickets: [],
            supportRequestorDrawerOpen: false,
            selectedSupportTicketId: ''
        };
    }

    async componentDidMount() {
        try {
            this.setState({ isLoading: true });
            await Promise.all([this.loadFilters(), this.loadUserProfile(), this.loadSupportTickets()]);
            this.loadSelectedSupportTicket();
        } catch (error) {
            Notification("error", "Error", error);
        } finally {
            this.setState({ isLoading: false });
        }
    }

    loadUserProfile = async () => {
        try {
            const response = await this.props.userActions.myProfile();
            if (response.type === types.LOAD_MY_PROFILE_SUCCESS) {
                const apiUser = response.payload.user;

                let departmentIds = ""
                if (apiUser.departmentIds && apiUser.departmentIds.length > 0) {
                    departmentIds = apiUser.departmentIds.join(',');
                }

                const countryId = (apiUser.countryId) ? apiUser.countryId.toString() : '';
                const defaultLocationId = (apiUser.defaultLocationId) ? apiUser.defaultLocationId.toString() : '';
                const managementCompanyId = (apiUser.managementCompanyId) ? apiUser.managementCompanyId.toString() : '';

                this.setState({
                    user:
                    {
                        ...this.state.user,
                        ...apiUser,
                        countryId: countryId,
                        defaultLocationId: defaultLocationId,
                        managementCompanyId: managementCompanyId,
                        departmentIds: departmentIds
                    }
                });
            } else if (response.type === types.LOAD_MY_PROFILE_FAILURE) {
                Notification("error", "An Error Occurred", response.payload.message);
            }
        } catch (error) {
            Notification("error", "Error", error);
        }
    }

    loadFilters = async () => {
        try {
            const response = await this.props.userActions.loadMyProfileFilters();
            if (response.type === types.LOAD_MY_PROFILE_FILTERS_SUCCESS) {
                this.setState({
                    ...this.state,
                    locations: response.payload.locations,
                    managementCompanies: response.payload.managementCompanies,
                    departments: response.payload.departments,
                    countries: response.payload.countries
                });
            } else if (response.type === types.LOAD_MY_PROFILE_FILTERS_FAILURE) {
                Notification("error", "An Error Occurred", response.payload.message);
            }
        } catch (error) {
            Notification("error", "Error", error);
        }
    }

    loadSupportTickets = async () => {
        try {
            const response = await this.props.userActions.getSupportTickets();
            if (response.type === types.GET_SUPPORT_TICKETS_SUCCESS) {
                const formattedTickets = response.payload.tickets.map(ticket =>
                ({
                    id: ticket.id,
                    createdDate: Moment.utc(ticket.createdDate).format(DATE_FORMAT),
                    status: ticket.status,
                    subject: ticket.subject
                })
                );

                this.setState({
                    supportTickets: formattedTickets
                })
            } else if (response.type === types.GET_SUPPORT_TICKETS_FAILURE) {
                Notification("error", "An Error Occurred", response.payload.message);
            }
        } catch (error) {
            Notification("error", "Error", error);
        }
    }

    loadSelectedSupportTicket = () => {
        const searchParams = new URLSearchParams(this.props.history.location.search);
        if (searchParams.has(SUPPORT_TICKET_ID_URL_PARAM.RequestorLink) && searchParams.getAll(SUPPORT_TICKET_ID_URL_PARAM.RequestorLink)) {
            window.location = "#support-tickets";
            this.setState({ selectedSupportTicketId: searchParams.getAll(SUPPORT_TICKET_ID_URL_PARAM.RequestorLink).toString(), supportRequestorDrawerOpen: true });
        }
    }

    onFormChange = (name, value) => {
        this.setState({
            ...this.state,
            user: {
                ...this.state.user,
                [name]: value
            }
        })
    }

    onAccountDetailsSave = async () => {
        if (!this.isAccountDetailFormValid()) {
            this.setState({ isAccountDetailFormValid: false });
            return;
        }

        try {
            this.setState({ isSavingAccountDetails: true });
            const result = await this.props.userActions.saveAccountDetails(this.state.user);

            if (result.type === types.SAVE_ACCOUNT_DETAILS_SUCCESS) {
                Notification("success", "Success", "Account details updated successfully");
                this.setState({ isAccountDetailFormValid: true });
            } else if (result.type === types.SAVE_ACCOUNT_DETAILS_FAILURE) {
                Notification("error", "Error", result.payload.message);
            }
        } catch (error) {
            Notification("error", "Error", error);
        } finally {
            this.setState({ isSavingAccountDetails: false });
        }
    }

    isAccountDetailFormValid = () => {
        const { firstName, lastName, userName, email, jobTitle } = this.state.user;

        if (!(email && email.trim().length > 0 && isValidEmailAddress(email))) {
            Notification("error", 'Email address is not valid', '');
            return false;
        }

        return firstName && firstName.trim().length > 0 &&
            lastName && lastName.trim().length > 0 &&
            userName && userName.trim().length > 0 &&
            jobTitle && jobTitle.trim().length > 0;
    }

    onConfigurationSave = async () => {
        if (!this.isConfigurationFormValid()) {
            this.setState({ isConfigurationFormValid: false });
            return;
        }

        try {
            this.setState({ isSavingConfiguration: true });
            const user = this.state.user;

            let departmentIds = [];
            if (user.departmentIds && user.departmentIds.trim().length > 0) {
                departmentIds = user.departmentIds.split(',');
            }

            const request = {
                id: user.id,
                subscriptionId: user.subscriptionId,
                countryId: user.countryId,
                defaultLocationId: user.defaultLocationId,
                managementCompanyId: user.managementCompanyId,
                departmentIds: departmentIds
            };

            const result = await this.props.userActions.saveConfiguration(request);

            if (result.type === types.SAVE_MY_PROFILE_CONFIGURATION_SUCCESS) {
                Notification("success", "Success", "Configuration updated successfully");
                this.setState({ isConfigurationFormValid: true });

                // update redux so the task list picks up a new default location
                const defaultLocationKey = this.state.locations.find(m => m.id.toString() === this.state.user.defaultLocationId).locationKey;
                this.props.locationTaskActions.setLocationKeys([defaultLocationKey]);

            } else if (result.type === types.SAVE_MY_PROFILE_CONFIGURATION_FAILURE) {
                Notification("error", "Error", result.payload.message);
            }
        } catch (error) {
            Notification("error", "Error", error);
        } finally {
            this.setState({ isSavingConfiguration: false });
        }
    }

    isConfigurationFormValid = () => {
        const { countryId, defaultLocationId } = this.state.user;

        return countryId && countryId.trim().length > 0 &&
            defaultLocationId && defaultLocationId.trim().length > 0;
    }

    onTicketClick = (selectedSupportTicketId) => {
        this.setState({ supportRequestorDrawerOpen: true, selectedSupportTicketId: selectedSupportTicketId });
    }

    renderSupportRequestorDrawer = () => {
        return <CustomerConversationDrawer ticketId={this.state.selectedSupportTicketId} onComplete={this.toggleSupportRequestorDrawer} onResolve={this.onResolveTicket} />;
    }

    toggleSupportRequestorDrawer = () => {
        this.setState({ supportRequestorDrawerOpen: !this.state.supportRequestorDrawerOpen });
    }

    onResolveTicket = async () => {
        await this.loadSupportTickets();
    }

    render() {
        return (
            <div className="my-profile-wrapper">
                <div className="my-profile">
                    <h2 className="my-profile-header">Profile Settings</h2>
                    <LoadingIndicator isLoading={this.state.isLoading}>
                        <LoadingIndicator isLoading={this.state.isSavingAccountDetails}>
                            <AccountDetailsSection
                                user={this.state.user}
                                isFormValid={this.state.isAccountDetailFormValid}
                                onFormChange={this.onFormChange}
                                onSaveChanges={this.onAccountDetailsSave} />
                        </LoadingIndicator>
                        <LoadingIndicator isLoading={this.state.isSavingConfiguration}>
                            <ConfigurationSection
                                user={this.state.user}
                                isFormValid={this.state.isConfigurationFormValid}
                                countries={this.state.countries}
                                locations={this.state.locations}
                                departments={this.state.departments}
                                managementCompanies={this.state.managementCompanies}
                                onFormChange={this.onFormChange}
                                onSaveChanges={this.onConfigurationSave} />
                        </LoadingIndicator>
                        {
                            this.state.user.isSSOUser
                                ?
                                null
                                :
                                <ResetPasswordSection
                                    userKey={this.state.user.userKey}
                                    accountActions={this.props.accountActions} />
                        }
                        {
                            this.state.supportTickets.length > 0 &&
                            <SupportTicketsSection
                                supportTickets={this.state.supportTickets}
                                onClick={this.onTicketClick} />
                        }
                    </LoadingIndicator>
                </div>

                {this.state.supportRequestorDrawerOpen &&
                    this.renderSupportRequestorDrawer()
                }
            </div>
        );
    }

}
MyProfile.propTypes = {
    userId: PropTypes.number.isRequired,
    subscriptionId: PropTypes.number.isRequired,
    userActions: PropTypes.object.isRequired,
    accountActions: PropTypes.object.isRequired,
    locationTaskActions: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired
};

function mapStateToProps(state, ownProps) {
    return {
        userId: state.user.id,
        subscriptionId: state.user.currentSubscriptionId,
        history: ownProps.history
    };
}

function mapDispatchToProps(dispatch) {
    return {
        userActions: bindActionCreators(userActions, dispatch),
        accountActions: bindActionCreators(accountActions, dispatch),
        locationTaskActions: bindActionCreators(locationTaskActions, dispatch)
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(MyProfile);

